import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Client } from '../types/index';

export class ClientService {
  /**
   * Create a new client
   */
  static async createClient(client: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>, userId: string): Promise<Client> {
    try {
      const clientRef = doc(collection(db, 'clients'));
      const now = new Date();

      const clientData: any = {
        ...client,
        createdBy: userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(clientRef, clientData);

      return {
        ...client,
        id: clientRef.id,
        createdBy: userId,
        createdAt: now,
        updatedAt: now,
      };
    } catch (error: any) {
      throw new Error(`Failed to create client: ${error.message}`);
    }
  }

  /**
   * Get a single client by ID
   */
  static async getClient(id: string): Promise<Client | null> {
    try {
      const docSnap = await getDoc(doc(db, 'clients', id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          createdAt: data.createdAt ? new Date(data.createdAt.toMillis()) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt.toMillis()) : new Date(),
        } as Client;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to get client: ${error.message}`);
    }
  }

  /**
   * Get all clients
   */
  static async getAllClients(): Promise<Client[]> {
    try {
      const q = query(collection(db, 'clients'), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt ? new Date(data.createdAt.toMillis()) : new Date(),
          updatedAt: data.updatedAt ? new Date(data.updatedAt.toMillis()) : new Date(),
        } as Client;
      });
    } catch (error: any) {
      throw new Error(`Failed to get clients: ${error.message}`);
    }
  }

  /**
   * Update a client
   */
  static async updateClient(id: string, updates: Partial<Client>): Promise<void> {
    try {
      const { id: _id, createdAt, updatedAt, createdBy, ...updateData } = updates as any;

      await updateDoc(doc(db, 'clients', id), {
        ...updateData,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update client: ${error.message}`);
    }
  }

  /**
   * Delete a client
   */
  static async deleteClient(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'clients', id));
    } catch (error: any) {
      throw new Error(`Failed to delete client: ${error.message}`);
    }
  }

  /**
   * Search clients by name
   */
  static async searchClients(searchTerm: string): Promise<Client[]> {
    try {
      const allClients = await this.getAllClients();
      const term = searchTerm.toLowerCase();

      return allClients.filter(client =>
        client.name.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.company?.toLowerCase().includes(term)
      );
    } catch (error: any) {
      throw new Error(`Failed to search clients: ${error.message}`);
    }
  }
}
